import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import type { GinSettingsResponse } from '@wedding-tech/api-client'
import { getApiV1WeddingsByWeddingIdSettingsOptions } from '@wedding-tech/api-client/tanstack'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { createContext, type ReactNode, useContext } from 'react'

dayjs.extend(isBetween)

interface SettingsContextValue {
  settings: GinSettingsResponse | undefined
  isLoading: boolean
  isCheckInOpen: boolean
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: undefined,
  isLoading: true,
  isCheckInOpen: true,
})

export function SettingsProvider({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as { eventId?: string }
  const weddingId = params.eventId ?? ''

  const { data: settingsRes, isLoading } = useQuery({
    ...getApiV1WeddingsByWeddingIdSettingsOptions({ path: { weddingId } }),
    enabled: !!weddingId,
  })
  const settings: any = (settingsRes as any)?.data ?? settingsRes

  const isCheckInOpen = (() => {
    if (!settings?.checkInDateRange) return false
    const { start, end } = settings.checkInDateRange as any
    const now = dayjs()
    if (start && end) return now.isBetween(dayjs(start), dayjs(end), null, '[]')
    if (start) return now.isAfter(dayjs(start))
    if (end) return now.isBefore(dayjs(end))
    return false
  })()

  return (
    <SettingsContext.Provider value={{ settings, isLoading, isCheckInOpen }}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettingsContext() {
  return useContext(SettingsContext)
}
